import axios from 'axios'
import {API_URL, fixNumbers} from '../../../../../../_metronic/helpers'
import {getBuyerEconomicCodeLength} from './buyerValidation'

/** One tax file of the buyer, as `POST customer/tax-files` (backend BuyerTaxFileRules) lists it. */
export type BuyerTaxFile = {
  economicCode?: string | null
  nationalId?: string | null
  /** نام تجاری; often empty. The official name comes from the form's own inquiry. */
  brand?: string | null
  taxFileStatus?: string | null
  taxFileStatusTitle?: string | null
  active: boolean
  callStatusTitle?: string | null
  registrationStatusTitle?: string | null
  /** 1 حقیقی، 2 حقوقی، 3 مشارکت مدنی، 4 اتباع؛ 0 when the identifiers do not say. */
  buyerType: number
  buyerTypeTitle?: string | null
  selectable: boolean
  warning?: string | null
  existingCustomerId?: number | null
  existingCustomerName?: string | null
}

export type BuyerTaxFileLookup = {
  identifier: string
  queriedBy: 'economicCode' | 'nationalId'
  notFound: boolean
  message?: string | null
  items: BuyerTaxFile[]
}

/**
 * Why the lookup gave no list. `correctable`: the identifier is the user's to fix. Otherwise the panel goes on the
 * old way (type, then the form); `silent` when the server is older than the lookup and simply has no such route.
 */
export type BuyerTaxFileFailure = {
  correctable: boolean
  silent?: boolean
  message: string
  /** The server's code; `organization_*` means the organisation was called, so the کارپوشه log has the details. */
  code?: string
  traceId?: string
}

/** What the buyer form starts from. */
export type BuyerPrefill = {
  economicCode?: string
  nationalCode?: string
  /** نام تجاری of the chosen file; the form takes it only when the official name inquiry gives none. */
  suggestedName?: string
  /** A tax file was chosen: the form asks the official name of its economic code at once. */
  fromTaxFile?: boolean
}

export const LOOKUP_FALLBACK_MESSAGE =
  'استعلام پرونده مالیاتی انجام نشد؛ نوع خریدار را انتخاب کنید و اطلاعات او را وارد کنید.'

const LOOKUP_INVALID_MESSAGE =
  'کد ملی (۱۰ رقم)، شناسه ملی (۱۱ رقم)، کد فراگیر اتباع (۱۲ رقم) یا شماره اقتصادی (۱۱ یا ۱۴ رقم) خریدار را بدون خط تیره وارد کنید.'

export const normalizeLookupIdentifier = (value?: string) =>
  fixNumbers(value || '').replace(/[^0-9]/g, '')

/** 10 کد ملی، 11 شناسه ملی یا شماره اقتصادی حقوقی، 12 کد فراگیر، 14 شماره اقتصادی حقیقی. */
export const isLookupIdentifier = (value?: string) =>
  [10, 11, 12, 14].includes(normalizeLookupIdentifier(value).length)

const createCorrelationId = () => {
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : undefined
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') return cryptoApi.randomUUID()
  const hex = () => Math.floor(Math.random() * 0x10000).toString(16).padStart(4, '0')
  return `${hex()}${hex()}-${hex()}-4${hex().slice(1)}-a${hex().slice(1)}-${hex()}${hex()}${hex()}`
}

export const lookUpBuyerTaxFiles = async (
  identifier: string,
  signal?: AbortSignal
): Promise<BuyerTaxFileLookup> => {
  const response = await axios.post(
    `${API_URL}/customer/tax-files`,
    {identifier: normalizeLookupIdentifier(identifier)},
    {headers: {'X-Correlation-ID': createCorrelationId()}, signal}
  )
  return response.data.data as BuyerTaxFileLookup
}

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : undefined

const safeTraceId = (value: unknown) =>
  typeof value === 'string' && /^[a-zA-Z0-9-]{1,64}$/.test(value.trim()) ? value.trim() : undefined

export const readBuyerTaxFileFailure = (error: unknown): BuyerTaxFileFailure => {
  const response = asRecord(asRecord(error)?.response)
  const status = typeof response?.status === 'number' ? response.status : undefined
  const data = asRecord(response?.data)
  const message = typeof data?.message === 'string' && data.message.trim() ? data.message.trim() : undefined
  const code = typeof data?.code === 'string' ? data.code : undefined
  const traceId = safeTraceId(data?.traceId)

  if (status === 400) {
    return {correctable: true, message: message || LOOKUP_INVALID_MESSAGE, code, traceId}
  }
  // A server older than the lookup: the old way, without a word about it.
  if (status === 404) {
    return {correctable: false, silent: true, message: ''}
  }
  return {correctable: false, message: message || LOOKUP_FALLBACK_MESSAGE, code, traceId}
}

/** The organisation was called and refused or did not answer: its exchange is in the business's کارپوشه log. */
export const isLoggedInTaxWorkspace = (failure?: BuyerTaxFileFailure) =>
  !!failure?.traceId && !!failure.code && failure.code.startsWith('organization_')

/** The form's identifiers from a chosen tax file. */
export const prefillFromTaxFile = (file: BuyerTaxFile): BuyerPrefill => ({
  economicCode: file.economicCode || undefined,
  nationalCode: file.nationalId || undefined,
  suggestedName: file.brand?.trim() || undefined,
  fromTaxFile: true,
})

const nationalIdentifierLength = (buyerType: number) =>
  buyerType === 2 ? 11 : buyerType === 4 ? 12 : 10

/**
 * Where a typed identifier goes once the user chose the type themselves (no tax file): the national identifier
 * field when its length fits the type, otherwise the economic code field, otherwise nowhere.
 */
export const placeIdentifier = (buyerType: number, identifier?: string): BuyerPrefill => {
  const digits = normalizeLookupIdentifier(identifier)
  if (!digits) return {}
  if (digits.length === nationalIdentifierLength(buyerType)) return {nationalCode: digits}
  if (digits.length === getBuyerEconomicCodeLength(buyerType)) return {economicCode: digits}
  return {}
}

/**
 * The type an identifier without a tax file points to, when only one is likely: 10 digits a person's national
 * code, 12 digits a foreigner's comprehensive code. 11 and 14 digits leave the choice to the user.
 */
export const typeOfIdentifierWithoutFile = (identifier?: string): number | undefined => {
  const length = normalizeLookupIdentifier(identifier).length
  return length === 10 ? 1 : length === 12 ? 4 : undefined
}

export const nationalIdentifierLabel = (buyerType: number) => {
  switch (buyerType) {
    case 1:
      return 'کد ملی'
    case 2:
      return 'شناسه ملی'
    case 3:
      return 'شناسه مشارکت مدنی'
    case 4:
      return 'کد فراگیر'
    default:
      return 'شناسه ملی / کد ملی'
  }
}
